import React, { useState, useEffect, useMemo } from 'react';
import {
  IFlowModelImportOptions,
  IImportable,
  ImportableFlowModel,
  ImportableFlowModelAttachments,
  ImportableFlowModelState
} from '@Client/runner.services/flow.model.import.service';
import { IFlowModelSummary } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelSummary';
import ImportableFlowModels from './ImportableFlowModels';
import { FlowModelPublishType } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelPublishType';
import { getService } from '@Client/runner.hooks/useService';
import { KendoReactFileUpload } from '@Shared.Angular/kendo.react/KendoReactFileUpload';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

type Props = {
  close: () => void;
};

type IImportOptionName =
  | 'createTeamsInFlowingly'
  | 'useReferenceNo'
  | 'preferExpert';

export const FlowModelImporter = ({ close }: Props) => {
  const [existingFlowModels, setExistingFlowModels] = useState<
    IFlowModelSummary[]
  >([]);
  const [disableImport, setDisableImport] = useState(true);
  const [importInProgress, setImportInProgress] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [importables, setimportables] = useState<IImportable[]>([]);
  const [importOptions, setImportOptions] = useState<IFlowModelImportOptions>({
    createTeamsInFlowingly: true,
    useReferenceNo: false,
    preferExpert: false
  });

  const flowModelImportService = getService('flowModelImportService');
  const APP_CONFIG = getService('APP_CONFIG');

  const isCategoryDepthExceeded = (importable: ImportableFlowModel) =>
    importable.categoryHierarchy.length > APP_CONFIG.maxSubCategories;

  const updateExistingFlowModelConflicts = (
    importableFlowModels: ImportableFlowModel[]
  ) => {
    importableFlowModels.forEach((importableFlowModel) => {
      const existingFlowModel = existingFlowModels.find(
        (flowModel) => flowModel.name === importableFlowModel.name
      );

      importableFlowModel.existingFlowModelId = existingFlowModel?.id;
      if (existingFlowModel) {
        importableFlowModel.willImport = false;
      }
    });
  };

  const updateImportButtonState = (updatedImportables: IImportable[]) => {
    const disableImportButton = !updatedImportables.some(
      (importable) =>
        importable instanceof ImportableFlowModel &&
        importable.willImport &&
        importable.state === ImportableFlowModelState.Pending
    );
    setDisableImport(disableImportButton);
  };

  useEffect(() => {
    getService('flowModelApiService')
      .getFlowModels()
      .then((flowModels) => {
        const processMaps = flowModels.filter(
          (fm) => fm.publishTypeValue === FlowModelPublishType.ProcessMap
        );
        setExistingFlowModels(processMaps);
      });
  }, []);

  const refreshImportables = (
    allFiles: File[],
    selectedImportOptions: IFlowModelImportOptions,
    shouldPreserveExistingSelections = true
  ) => {
    return flowModelImportService
      .getImportableFlowModels(allFiles, selectedImportOptions)
      .then((newImportables) => {
        if (shouldPreserveExistingSelections) {
          importables.forEach((oldImportable) => {
            const newImportable = newImportables.find(
              (importable) => importable.key === oldImportable.key
            );
            if (newImportable) {
              newImportable.state = oldImportable.state;
              newImportable.willImport = oldImportable.willImport;
            }
          });
        }

        const importableFlowModels = newImportables.filter(
          (importable): importable is ImportableFlowModel =>
            importable instanceof ImportableFlowModel
        );
        newImportables
          .filter(
            (importable): importable is ImportableFlowModelAttachments =>
              importable instanceof ImportableFlowModelAttachments
          )
          .forEach((flowModelAttachments) => {
            if (flowModelAttachments.key.endsWith('.zip')) {
              const fileRegex = /<p>\[([^|]+)\|([^|]+)\|([^|]+)\]<\/p>/g;
              importableFlowModels.forEach((flowModel) => {
                if (!flowModel.flowinglyNodes) {
                  return;
                }
                const fileData = flowModel.flowinglyNodes.flatMap((node) =>
                  node.Card.formElements.flatMap((field) =>
                    [...field.value.matchAll(fileRegex)].map((match) => {
                      return {
                        type: match[1],
                        name: match[2],
                        normalizedName:
                          flowModelImportService.normalizeForComparison(
                            match[1],
                            match[2]
                          ),
                        id: match[3]
                      };
                    })
                  )
                );
                const files = flowModelAttachments.attachments.filter((a) => {
                  const matchingFileData = fileData.find(
                    (fd) =>
                      fd.name === a.htmlEncodedName ||
                      fd.normalizedName ===
                        flowModelImportService.normalizeForComparison(
                          fd.type,
                          a.htmlEncodedName
                        )
                  );
                  if (matchingFileData && matchingFileData.type === 'Video') {
                    a.name = matchingFileData.id;
                  }
                  return matchingFileData !== undefined;
                });
                flowModel.attachments = files;
              });
            } else {
              const flowModel = importableFlowModels.find(
                (importableFlowModel) =>
                  importableFlowModel.uniqueId === flowModelAttachments.key
              );
              if (flowModel) {
                flowModel.attachments = flowModelAttachments.attachments;
              }
            }
          });

        updateExistingFlowModelConflicts(importableFlowModels);

        setimportables(newImportables);
        updateImportButtonState(newImportables);
      });
  };

  const addNewFiles = (incomingFiles: File[]) => {
    setDisableImport(true);
    const newFiles = incomingFiles.filter((incomingFile) => {
      return !files.some(
        (existingFile) =>
          existingFile.name === incomingFile.name &&
          existingFile.size === incomingFile.size
      );
    });
    const allFiles = newFiles.concat(files);
    setFiles(allFiles);
    refreshImportables(allFiles, importOptions);
  };

  const importableUpdated = (
    index: number,
    propertyName: string,
    propertyValue: boolean
  ) => {
    const importable = importables[index];
    importable[propertyName] = propertyValue;
    setimportables([...importables]);
    updateImportButtonState(importables);
  };

  const updateImportOption = (
    optionName: IImportOptionName,
    optionValue: boolean
  ) => {
    const updatedImportOptions = {
      ...importOptions,
      [optionName]: optionValue
    };

    setImportOptions(updatedImportOptions);

    if (optionName === 'createTeamsInFlowingly') {
      return;
    }

    refreshImportables(files, updatedImportOptions);
  };

  const importSelectedFlowModels = async () => {
    if (isCategoryDepthLimitExceeded) {
      setDisableImport(true);
      return;
    }

    setDisableImport(true);
    setImportInProgress(true);
    const updater = (updatedImportables: ImportableFlowModel[]) =>
      setimportables([...updatedImportables]);
    const importableFlowModels = importables.filter(
      (importable): importable is ImportableFlowModel =>
        importable instanceof ImportableFlowModel
    );
    await flowModelImportService.importFlowModels(
      importableFlowModels,
      updater,
      importOptions
    );

    setImportInProgress(false);
    if (
      importables.some(
        (importable) =>
          importable.willImport &&
          importable.state === ImportableFlowModelState.Pending
      )
    ) {
      setDisableImport(false);
    }
  };

  const updateFlowModelOwners = async () => {
    setImportInProgress(true);
    const importableFlowModels = importables.filter(
      (importable): importable is ImportableFlowModel =>
        importable instanceof ImportableFlowModel
    );
    await flowModelImportService.updateFlowModelOwners(importableFlowModels);
    getService('notificationService').showSuccessToast('Owners updated');
    setImportInProgress(false);
  };

  const importableFlowModels = useMemo(
    () =>
      importables.filter(
        (importable): importable is ImportableFlowModel =>
          importable instanceof ImportableFlowModel
      ),
    [importables]
  );
  // select the deepest category from each importable flow model
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    importableFlowModels.forEach((importable) => {
      if (importable.categoryHierarchy?.length > 0) {
        categorySet.add(importable.categoryHierarchy.at(-1).name);
      }
    });
    return categorySet;
  }, [importableFlowModels]);

  const isCategoryDepthLimitExceeded = useMemo(
    () =>
      importableFlowModels.some((importable) =>
        isCategoryDepthExceeded(importable)
      ),
    [importableFlowModels]
  );

  const categoryDepthWarning = isCategoryDepthLimitExceeded
    ? `One or more Flow Models have a Category hierarchy deeper than the current MaxSubCategories limit (${APP_CONFIG.maxSubCategories}). Increase MaxSubCategories in Business Settings before importing.`
    : null;

  const unresolvedProcessOwners = useMemo(() => {
    const unresolvedOwners: {
      key: string;
      processName: string;
      userName: string;
    }[] = [];

    importableFlowModels.forEach((importable) => {
      (importable.unresolvedProcessOwnerNames || []).forEach((ownerName) => {
        unresolvedOwners.push({
          key: `${importable.key}-${ownerName}`,
          processName: importable.name,
          userName: ownerName
        });
      });
    });

    return unresolvedOwners;
  }, [importableFlowModels]);

  const toggleAllImports = () => {
    if (isCategoryDepthLimitExceeded) {
      setDisableImport(true);
      return;
    }

    const importableFlowModel = importableFlowModels.find(
      (f) =>
        !f.existingFlowModelId && f.state === ImportableFlowModelState.Pending
    );
    if (!importableFlowModel) {
      setDisableImport(true);
      return;
    }
    const newToggleValue = importableFlowModel?.willImport === false;
    importableFlowModels.forEach((importable) => {
      if (
        importable.existingFlowModelId ||
        isCategoryDepthExceeded(importable)
      ) {
        importable.willImport = false;
        return;
      }
      importable.willImport = newToggleValue;
    });
    setimportables([...importables]);
    updateImportButtonState(importables);
  };

  return (
    <div className="flow-models-importer">
      <div className="import-options">
        <div
          className="import-option-toggle"
          title="Create Teams in Flowingly that are referenced by imported processes"
        >
          <input
            type="checkbox"
            id="import-create-teams"
            data-testid="import-create-teams-checkbox"
            checked={importOptions.createTeamsInFlowingly}
            disabled={importInProgress}
            onChange={(event) =>
              updateImportOption('createTeamsInFlowingly', event.target.checked)
            }
          />
          <label htmlFor="import-create-teams">Create Teams</label>
        </div>
        <div
          className="import-option-toggle"
          title="Prefix Flow Model name with process ReferenceNo"
        >
          <input
            type="checkbox"
            id="import-use-reference-no"
            data-testid="import-use-reference-no-checkbox"
            checked={importOptions.useReferenceNo}
            disabled={importInProgress}
            onChange={(event) =>
              updateImportOption('useReferenceNo', event.target.checked)
            }
          />
          <label htmlFor="import-use-reference-no">Use ReferenceNo</label>
        </div>
        <div
          className="import-option-toggle"
          title="Prefer using process Expert over Owner to set Flow Model Process Owner"
        >
          <input
            type="checkbox"
            id="import-prefer-expert"
            data-testid="import-prefer-expert-checkbox"
            checked={importOptions.preferExpert}
            disabled={importInProgress}
            onChange={(event) =>
              updateImportOption('preferExpert', event.target.checked)
            }
          />
          <label htmlFor="import-prefer-expert">Prefer Expert</label>
        </div>
      </div>
      <div hidden={importInProgress}>
        <KendoReactFileUpload
          acceptedFileExtensions={['.xml', '.vsdx', '.zip']}
          multiple={true}
          selectButtonText="Upload importable files (.xml, .vsdx, .zip)"
          onFilesAdded={addNewFiles}
        />
      </div>
      <div className="p-30" hidden={!importInProgress}>
        Importing... please wait until this message disappears.
      </div>
      <div>
        {importableFlowModels.length > 0 && (
          <div className="importable-flow-models-header">
            <div className="importable-flow-models-count">
              {importableFlowModels.length} Flow Models
            </div>
            <div className="importable-categories-count">
              {categories.size} Categories
            </div>
            <TextButton
              text="Toggle All"
              type="secondary"
              onClick={toggleAllImports}
              disabled={isCategoryDepthLimitExceeded || importInProgress}
            />
          </div>
        )}
        {categoryDepthWarning && (
          <div
            className="category-depth-warning"
            data-testid="category-depth-warning"
          >
            {categoryDepthWarning}
          </div>
        )}
        <div className="importable-flow-models">
          {importableFlowModels.map((importable, index) => (
            <ImportableFlowModels
              importable={importable}
              key={importable.key}
              index={index}
              maxCategoryDepth={APP_CONFIG.maxSubCategories}
              disableSelection={
                isCategoryDepthLimitExceeded || importInProgress
              }
              updateParent={importableUpdated}
            />
          ))}
        </div>
        {unresolvedProcessOwners.length > 0 && (
          <div className="importable-owner-warnings">
            <div className="importable-owner-warnings-title">
              Users not found in this business
            </div>
            <ul>
              {unresolvedProcessOwners.map((unresolvedOwner) => (
                <li key={unresolvedOwner.key}>
                  {unresolvedOwner.userName} ({unresolvedOwner.processName})
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="import-action">
          <TextButton
            text="Close"
            type="text-only"
            onClick={close}
            disabled={importInProgress}
          />
          <TextButton
            text="Update Owners"
            type="secondary"
            disabled={importInProgress || importables.length === 0}
            onClick={updateFlowModelOwners}
          />
          <TextButton
            text="Import"
            type="primary"
            disabled={disableImport || isCategoryDepthLimitExceeded}
            onClick={importSelectedFlowModels}
          />
        </div>
      </div>
    </div>
  );
};

export default FlowModelImporter;
